import * as Sentry from '@sentry/react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Building2, Plus, Search, LayoutGrid, List as ListIcon, Shield, Calendar, MoreVertical, ExternalLink, Loader2, Settings2, Check, Zap, Trash2 } from 'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { adminApi, gymApi, billingApi } from '@/lib/api';
import { Gym, PlanType, Status, MembershipPlan, SaasTier } from '@/types';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { CreateGymWizard } from '@/components/CreateGymWizard';
import { ALL_MODULES } from '@/lib/constants';

export default function GymsPage() {
  const { setSelectedGym, gym: activeGym } = useAuth();
  const [gyms, setGyms] = useState<Gym[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [editingGym, setEditingGym] = useState<Gym | null>(null);
  const [deletingGymId, setDeletingGymId] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    Sentry.setTag("area", "system");
  }, []);
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('gyms');
  const [isTierDialogOpen, setIsTierDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<SaasTier | null>(null);
  const [tierFormData, setTierFormData] = useState({
    name: '',
    price: 0,
    included_modules: [] as string[],
    is_private: false
  });

  const [formData, setFormData] = useState({
    name: '',
    plan: 'basic' as PlanType,
    tier_id: '',
    expiry_date: '',
    status: 'active' as Status,
    modules_enabled: ['clients', 'memberships', 'scanner'],
    price: 0,
    is_trial: true,
  });

  const [saasTiers, setSaasTiers] = useState<SaasTier[]>([]);

  useEffect(() => {
    fetchGyms();
    fetchSaasTiers();
  }, []);

  const fetchSaasTiers = async () => {
    try {
      const { data, error } = await supabase
        .from('saas_tiers')
        .select('*')
        .order('price', { ascending: true });

      if (data) setSaasTiers(data);
    } catch (error) {
      console.error('Failed to fetch SaaS tiers:', error);
    }
  };

  const fetchGyms = async () => {
    try {
      const res = await adminApi.listGyms();
      if (res.success) {
        setGyms(res.gyms);
      }
    } catch (error) {
      console.error('Failed to fetch gyms:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los gimnasios',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  // handleCreateGym logic is now inside CreateGymWizard component

  const handleUpdateGym = async () => {
    if (!editingGym) return;

    setIsSubmitting(true);
    try {
      const res = await adminApi.updateGym({
        ...formData,
        gym_id: editingGym.gym_id
      });

      if (res.success) {
        await fetchGyms();
        setIsEditOpen(false);
        setEditingGym(null);
        toast({
          title: 'Gimnasio actualizado',
          description: 'Los cambios han sido guardados correctamente',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo actualizar el gimnasio',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteGym = async (gymId: string) => {
    setIsSubmitting(true);
    try {
      const res = await adminApi.deleteGym(gymId);
      if (res.success) {
        await fetchGyms();
        setIsDeleteOpen(false);
        setDeletingGymId(null);
        toast({
          title: 'Gimnasio eliminado',
          description: 'El establecimiento ha sido eliminado permanentemente',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo eliminar el gimnasio',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleManageGym = (gym: Gym) => {
    setSelectedGym(gym);
    toast({
      title: 'Gimnasio seleccionado',
      description: `Ahora estás gestionando: ${gym.name}`,
    });
    navigate('/dashboard');
  };

  const openEditDialog = (gym: Gym) => {
    setEditingGym(gym);
    setFormData({
      name: gym.name,
      plan: gym.plan,
      tier_id: gym.tier_id || '',
      expiry_date: gym.expiry_date || '',
      status: gym.status,
      modules_enabled: gym.modules_enabled,
      price: gym.price || 0,
      is_trial: gym.is_trial ?? true,
    });
    setIsEditOpen(true);
  };

  const openTierEditDialog = (tier: SaasTier) => {
    setEditingTier(tier);
    setTierFormData({
      name: tier.name,
      price: Number(tier.price),
      included_modules: tier.included_modules,
      is_private: !!tier.is_private
    });
    setIsTierDialogOpen(true);
  };

  const openCreateTierDialog = () => {
    setEditingTier(null);
    setTierFormData({
      name: '',
      price: 0,
      included_modules: ['clients', 'memberships'],
      is_private: false
    });
    setIsTierDialogOpen(true);
  };

  const handleSaveSaasTier = async () => {
    setIsSubmitting(true);
    try {
      if (editingTier) {
        const res = await adminApi.updateSaasTier(editingTier.id, tierFormData);
        if (res.success) {
          toast({
            title: 'Tier actualizado',
            description: `El plan ${tierFormData.name} ha sido actualizado correctamente.`,
          });
        }
      } else {
        const res = await adminApi.createSaasTier(tierFormData);
        if (res.success) {
          toast({
            title: 'Tier creado',
            description: `El plan ${tierFormData.name} ha sido creado correctamente.`,
          });
        }
      }
      await fetchSaasTiers();
      setIsTierDialogOpen(false);
    } catch (error) {
      toast({
        title: 'Error',
        description: `No se pudo ${editingTier ? 'actualizar' : 'crear'} el tier`,
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSaasTier = async (tierId: string) => {
    if (!window.confirm('¿Estás seguro de eliminar este tier?')) return;
    setIsSubmitting(true);
    try {
      const res = await adminApi.deleteSaasTier(tierId);
      if (res.success) {
        await fetchSaasTiers();
        toast({
          title: 'Tier eliminado',
          description: 'El plan ha sido eliminado permanentemente.',
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudo eliminar el tier',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredGyms = gyms.filter(gym =>
    gym.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    gym.gym_id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getPlanBadge = (plan: string) => {
    const isSpecial = ['enterprise', 'professional', 'basic'].includes(plan.toLowerCase());

    if (plan.toLowerCase() === 'enterprise') {
      return <Badge className="bg-purple-500/10 text-purple-500 border-purple-500/20">Enterprise</Badge>;
    }
    if (plan.toLowerCase() === 'professional') {
      return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Professional</Badge>;
    }
    if (plan.toLowerCase() === 'basic') {
      return <Badge variant="secondary">Basic</Badge>;
    }

    return <Badge className="bg-primary/10 text-primary border-primary/20">{plan}</Badge>;
  };

  const getStatusBadge = (status: Status) => {
    switch (status) {
      case 'active':
        return <Badge variant="success">Activo</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200">Pendiente</Badge>;
      case 'expired':
        return <Badge variant="destructive">Expirado</Badge>;
      case 'inactive':
        return <Badge variant="muted">Inactivo</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getTrialBadge = (isTrial: boolean) => {
    if (isTrial) {
      return (
        <Badge className="bg-orange-500/10 text-orange-600 border-orange-200/50 flex items-center gap-1 shrink-0">
          <Zap className="h-3 w-3 fill-orange-500" /> PRUEBA
        </Badge>
      );
    }
    return (
      <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-200/50 flex items-center gap-1 shrink-0">
        <Check className="h-3 w-3" /> CLIENTE
      </Badge>
    );
  };

  const getSetupBadge = (gym: Gym) => {
    if (gym.setup_fee_paid) {
      return (
        <Badge variant="outline" className="bg-emerald-50/50 text-emerald-700 border-emerald-200/50 flex items-center gap-1 shrink-0 text-[9px]">
          <Check className="h-2.5 w-2.5" /> SETUP OK
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-amber-50/50 text-amber-700 border-amber-200/50 flex items-center gap-1 shrink-0 text-[9px]">
        <Zap className="h-2.5 w-2.5" /> SETUP PEND.
      </Badge>
    );
  };

  const handleToggleSetupPaid = async (gym: Gym) => {
    try {
      if (gym.setup_fee_paid) {
        await billingApi.unmarkSetupPaid(gym.gym_id);
        toast({ title: 'Setup desmarcado', description: `${gym.name} marcado como setup pendiente.` });
      } else {
        await billingApi.markSetupPaid(gym.gym_id);
        toast({ title: 'Setup pagado', description: `$${gym.setup_fee_amount ?? 79} registrados como cobrados en ${gym.name}.` });
      }
      await fetchGyms();
    } catch (err: any) {
      toast({ title: 'Error', description: err?.message || 'No se pudo actualizar el setup', variant: 'destructive' });
    }
  };

  const handleApproveGym = async (gymId: string) => {
    try {
      const { error } = await supabase.from('gyms').update({ status: 'active' }).eq('id', gymId);
      if (error) throw error;
      toast({ title: 'Gimnasio aprobado', description: 'El gimnasio ahora puede operar normalmente.' });
      await fetchGyms();
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudo aprobar el gimnasio', variant: 'destructive' });
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout title="Gimnasios">
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Gimnasios"
      subtitle="Administración global de establecimientos"
      actions={
        activeTab === 'gyms' && (
          <Button onClick={() => setIsDialogOpen(true)} variant="gradient">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Gimnasio
          </Button>
        )
      }
    >
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 max-w-[400px]">
          <TabsTrigger value="gyms">Gimnasios</TabsTrigger>
          <TabsTrigger value="tiers">Tiers SaaS</TabsTrigger>
        </TabsList>

        <TabsContent value="gyms" className="space-y-6">
          {/* Filters and Search */}
          <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
            <div className="relative w-full md:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o ID..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 border rounded-lg p-1 bg-muted/30">
              <Button
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="h-8 w-8 p-0"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="h-8 w-8 p-0"
              >
                <ListIcon className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* View Content */}
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGyms.map((gym) => (
                <Card key={gym.gym_id} className={cn(
                  "glass-card hover:shadow-lg transition-all",
                  activeGym?.gym_id === gym.gym_id && "ring-2 ring-primary border-primary"
                )}>
                  <CardHeader className="pb-3 border-b border-border/50 mb-4">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg gradient-primary flex items-center justify-center">
                          <Building2 className="h-6 w-6 text-primary-foreground" />
                        </div>
                        <div className="flex flex-col">
                          <CardTitle className="text-lg truncate max-w-[150px]">{gym.name}</CardTitle>
                          <div className="flex items-center gap-2 flex-wrap">
                            <CardDescription className="text-[10px] font-mono">ID: {gym.gym_id.substring(0, 8)}...</CardDescription>
                            {getTrialBadge(gym.is_trial)}
                            {getSetupBadge(gym)}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleToggleSetupPaid(gym)}
                          title={gym.setup_fee_paid ? 'Setup pagado (click para desmarcar)' : 'Marcar setup como pagado'}
                          className={gym.setup_fee_paid ? 'text-emerald-600' : 'text-amber-600'}
                        >
                          {gym.setup_fee_paid ? <Check className="h-4 w-4" /> : <Zap className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(gym)}>
                          <Settings2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-500 hover:bg-red-500/10"
                          onClick={() => { setDeletingGymId(gym.gym_id); setIsDeleteOpen(true); }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-[10px] uppercase text-muted-foreground font-bold">Plan</p>
                        {getPlanBadge(gym.plan)}
                      </div>
                      <div className="space-y-1 text-right">
                        <p className="text-[10px] uppercase text-muted-foreground font-bold">Estado</p>
                        {getStatusBadge(gym.status)}
                      </div>
                    </div>

                    <div className="space-y-2 pt-2">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          <span>Vencimiento:</span>
                        </div>
                        <span className="font-medium">{gym.expiry_date}</span>
                      </div>
                    </div>

                    <div className="pt-2">
                      <p className="text-[10px] uppercase text-muted-foreground font-bold mb-2">Módulos Habilitados</p>
                      <div className="flex flex-wrap gap-1">
                        {gym.modules_enabled?.slice(0, 3).map(m => (
                          <Badge key={m} variant="secondary" className="text-[9px] h-4">
                            {m === 'clients' ? 'Clientes' : m === 'memberships' ? 'Membresías' : m === 'scanner' ? 'Escáner' : m}
                          </Badge>
                        ))}
                        {(gym.modules_enabled?.length || 0) > 3 && (
                          <Badge variant="outline" className="text-[9px] h-4">
                            +{gym.modules_enabled.length - 3}
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4">
                      {gym.status === 'pending' && (
                        <Button
                          className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-bold"
                          onClick={() => handleApproveGym(gym.gym_id)}
                        >
                          <Check className="h-4 w-4 mr-2" />
                          Aprobar
                        </Button>
                      )}
                      <Button
                        className={cn("flex-1", gym.status === 'pending' && "variant-outline")}
                        variant={gym.status === 'pending' ? 'outline' : 'gradient'}
                        onClick={() => handleManageGym(gym)}
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Gestionar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left p-4 font-bold">Gimnasio</th>
                        <th className="text-left p-4 font-bold">Plan</th>
                        <th className="text-left p-4 font-bold">Estado</th>
                        <th className="text-left p-4 font-bold">Vencimiento</th>
                        <th className="text-right p-4 font-bold">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredGyms.map((gym) => (
                        <tr key={gym.gym_id} className="border-b hover:bg-muted/30 transition-colors">
                          <td className="p-4">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Building2 className="h-4 w-4 text-primary" />
                              <span className="font-medium">{gym.name}</span>
                              {getTrialBadge(gym.is_trial)}
                              {getSetupBadge(gym)}
                            </div>
                          </td>
                          <td className="p-4">{getPlanBadge(gym.plan)}</td>
                          <td className="p-4">{getStatusBadge(gym.status)}</td>
                          <td className="p-4 text-muted-foreground">{gym.expiry_date}</td>
                          <td className="p-4 text-right">
                            <div className="flex justify-end gap-2">
                              {gym.status === 'pending' && (
                                <Button
                                  size="sm"
                                  className="bg-orange-600 hover:bg-orange-700 text-white font-bold"
                                  onClick={() => handleApproveGym(gym.gym_id)}
                                >
                                  Aprobar
                                </Button>
                              )}
                              <Button variant="ghost" size="icon" onClick={() => openEditDialog(gym)}>
                                <Settings2 className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-red-500 hover:bg-red-500/10"
                                onClick={() => { setDeletingGymId(gym.gym_id); setIsDeleteOpen(true); }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleManageGym(gym)}
                              >
                                Gestionar
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {filteredGyms.length === 0 && (
            <div className="text-center py-20 bg-muted/20 rounded-xl border-2 border-dashed">
              <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-20" />
              <h3 className="text-lg font-bold mb-1">No se encontraron gimnasios</h3>
              <p className="text-muted-foreground">Prueba con otro término de búsqueda o crea uno nuevo</p>
              <Button onClick={() => setIsDialogOpen(true)} variant="outline" className="mt-6">
                <Plus className="h-4 w-4 mr-2" />
                Crear primer gimnasio
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value="tiers" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">Planes SaaS para Dueños</h3>
              <p className="text-sm text-muted-foreground">Configura los paquetes que ofreces a los gimnasios.</p>
            </div>
            <Button onClick={openCreateTierDialog} variant="gradient">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Tier
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {saasTiers.map((tier) => (
              <Card key={tier.id} className="glass-card hover:shadow-lg transition-all border-primary/20">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-xl font-bold text-primary">{tier.name}</CardTitle>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openTierEditDialog(tier)}>
                        <Settings2 className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-500 hover:bg-red-500/10"
                        onClick={() => handleDeleteSaasTier(tier.id)}
                        disabled={isSubmitting}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="px-6 pb-2">
                    <div className="text-2xl font-black text-foreground">
                      ${tier.price} <span className="text-xs font-normal text-muted-foreground">/mes</span>
                      {tier.is_private && (
                        <Badge variant="outline" className="text-[10px] bg-amber-500/10 text-amber-600 border-amber-200 ml-2">Privado</Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-xs font-bold uppercase text-muted-foreground">Módulos Incluidos:</p>
                    <div className="flex flex-wrap gap-1">
                      {tier.included_modules.map(mod => (
                        <Badge key={mod} variant="secondary" className="text-[10px]">
                          {ALL_MODULES.find(m => m.module_id === mod)?.name || mod}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="pt-4">
                    <Button variant="outline" className="w-full" onClick={() => openTierEditDialog(tier)}>
                      Modificar Plan
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Gym Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Provisionamiento de Nuevo Gimnasio</DialogTitle>
            <DialogDescription>
              Sigue los pasos para aprovisionar un nuevo entorno de gimnasio.
            </DialogDescription>
          </DialogHeader>
          <CreateGymWizard
            onSuccess={() => {
              setIsDialogOpen(false);
              fetchGyms();
            }}
            onCancel={() => setIsDialogOpen(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Gimnasio</DialogTitle>
            <DialogDescription>
              Modifica los detalles del establecimiento y su plan.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Nombre del Gimnasio</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Nombre del gimnasio"
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-tier" className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-primary" /> Tier SaaS (Paquete del Dueño)
              </Label>
              <Select
                value={formData.tier_id}
                onValueChange={(value) => {
                  const tier = saasTiers.find(t => t.id === value);
                  if (tier) {
                    setFormData(prev => ({
                      ...prev,
                      tier_id: tier.id,
                      plan: tier.name,
                      price: tier.price,
                      modules_enabled: tier.included_modules || prev.modules_enabled,
                      // If current status is pending, assume approval when assigning a tier
                      status: prev.status === 'pending' ? 'active' : prev.status
                    }));
                    toast({
                      title: 'Tier asignado',
                      description: formData.status === 'pending'
                        ? `Se ha aplicado el nivel "${tier.name}" y se ha marcado como Activo.`
                        : `Se ha aplicado el nivel "${tier.name}" con su precio y módulos asociados.`,
                    });
                  }
                }}
                disabled={isSubmitting}
              >
                <SelectTrigger className="border-primary/20 bg-primary/5">
                  <SelectValue placeholder="Seleccionar tier" />
                </SelectTrigger>
                <SelectContent>
                  {saasTiers
                    .map(tier => (
                      <SelectItem key={tier.id} value={tier.id}>
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-bold">{tier.name}</span>
                            {tier.is_private && <Badge variant="outline" className="h-3 text-[8px] px-1 border-amber-200 text-amber-600">PRIVADO</Badge>}
                          </div>
                          <span className="text-[10px] opacity-70">${tier.price}/mes</span>
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground">Al cambiar el tier se actualizan automáticamente el precio y los módulos habilitados para el dueño.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-expiry">Fecha de Vencimiento</Label>
              <Input
                id="edit-expiry"
                type="date"
                value={formData.expiry_date}
                onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-status">Estado</Label>
              <Select
                value={formData.status}
                onValueChange={(value: Status) => setFormData(prev => ({ ...prev, status: value }))}
                disabled={isSubmitting}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar estado" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Activo</SelectItem>
                  <SelectItem value="inactive">Inactivo</SelectItem>
                  <SelectItem value="expired">Expirado</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3 pt-2">
              <Label className="text-xs font-bold uppercase text-muted-foreground">Módulos Activos</Label>
              <div className="grid grid-cols-2 gap-2">
                {ALL_MODULES.map((mod) => (
                  <Badge
                    key={mod.module_id}
                    variant={formData.modules_enabled.includes(mod.module_id) ? 'default' : 'outline'}
                    className="cursor-pointer capitalize py-1.5 justify-center border-dashed"
                    onClick={() => {
                      if (formData.modules_enabled.includes(mod.module_id)) {
                        setFormData(prev => ({
                          ...prev,
                          modules_enabled: prev.modules_enabled.filter(m => m !== mod.module_id)
                        }));
                      } else {
                        setFormData(prev => ({
                          ...prev,
                          modules_enabled: [...prev.modules_enabled, mod.module_id]
                        }));
                      }
                    }}
                  >
                    {mod.name}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between p-4 rounded-2xl bg-muted/30 border border-muted-foreground/10">
              <div className="space-y-0.5">
                <Label className="text-sm font-bold">Estado de Prueba (Trial)</Label>
                <p className="text-[10px] text-muted-foreground">Marca si el gimnasio aún está en su periodo gratuito.</p>
              </div>
              <Switch
                checked={formData.is_trial}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_trial: checked }))}
                disabled={isSubmitting}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsEditOpen(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateGym} variant="gradient" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                'Guardar Cambios'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isTierDialogOpen} onOpenChange={setIsTierDialogOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTier ? 'Editar Tier SaaS' : 'Nuevo Tier SaaS'}</DialogTitle>
            <DialogDescription>
              {editingTier
                ? `Modifica los beneficios y el precio del plan "${tierFormData.name}".`
                : "Configura un nuevo paquete de servicios para ofrecer a los gimnasios."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nombre del Tier</Label>
              <Input
                value={tierFormData.name}
                onChange={(e) => setTierFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Básico"
              />
            </div>
            <div className="space-y-2">
              <Label>Precio Mensual ($)</Label>
              <Input
                type="number"
                value={tierFormData.price}
                onChange={(e) => setTierFormData(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
              />
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg border bg-muted/30">
              <div className="space-y-0.5">
                <Label className="text-sm font-bold">Plan Privado</Label>
                <p className="text-[10px] text-muted-foreground">Ocultar de las listas de creación de nuevos gimnasios.</p>
              </div>
              <Switch
                checked={tierFormData.is_private}
                onCheckedChange={(checked) => setTierFormData(prev => ({ ...prev, is_private: checked }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Módulos Incluidos</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {ALL_MODULES.map(mod => {
                  const isIncluded = tierFormData.included_modules.includes(mod.module_id);
                  return (
                    <div
                      key={mod.module_id}
                      className={cn(
                        "flex items-center gap-2 p-2 border rounded cursor-pointer transition-colors",
                        isIncluded ? "bg-primary/5 border-primary" : "hover:bg-muted"
                      )}
                      onClick={() => {
                        const newModules = isIncluded
                          ? tierFormData.included_modules.filter(m => m !== mod.module_id)
                          : [...tierFormData.included_modules, mod.module_id];
                        setTierFormData(prev => ({ ...prev, included_modules: newModules }));
                      }}
                    >
                      <div className={cn(
                        "w-4 h-4 rounded border flex items-center justify-center",
                        isIncluded ? "bg-primary border-primary" : "border-muted-foreground/30"
                      )}>
                        {isIncluded && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <span className="text-xs">{mod.name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setIsTierDialogOpen(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button onClick={handleSaveSaasTier} variant="gradient" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Guardando...
                </>
              ) : (
                editingTier ? 'Guardar Cambios' : 'Crear Tier'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>¿Eliminar gimnasio?</DialogTitle>
            <DialogDescription>
              Esta acción es irreversible y eliminará todos los datos asociados (socios, membresías, etc.) de este gimnasio.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setIsDeleteOpen(false)} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingGymId && handleDeleteGym(deletingGymId)}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin text-white" /> : 'Eliminar Permanentemente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
